/**
 * SecurityTreeFilterBar — Geographic filter dropdowns + text search.
 *
 * Allows users to narrow the tree view by Area/Country/Region and
 * search nodes by name.
 */

import { useMemo } from 'react';
import Button from '@mui/material/Button';
import type { TreeFilter, TreeNode } from './types';
import { TextInput, SelectInput } from '../ui';

interface FilterBarProps {
  tree: Map<string, TreeNode>;
  filter: TreeFilter;
  onFilterChange: (filter: TreeFilter) => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
}

interface FilterOption {
  id: number;
  label: string;
}

export function SecurityTreeFilterBar({
  tree,
  filter,
  onFilterChange,
  searchTerm,
  onSearchChange,
}: FilterBarProps) {
  const areas = useMemo<FilterOption[]>(() => {
    const result: FilterOption[] = [];
    for (const node of tree.values()) {
      if (node.nodeType === 'area') {
        result.push({ id: node.originId, label: node.label });
      }
    }
    return result.sort((a, b) => a.label.localeCompare(b.label));
  }, [tree]);

  const countries = useMemo<FilterOption[]>(() => {
    const result: FilterOption[] = [];
    for (const node of tree.values()) {
      if (node.nodeType === 'country') {
        // If area filter is active, only show countries under that area
        if (filter.areaId) {
          const parent = node.parentId ? tree.get(node.parentId) : null;
          if (parent?.originId !== filter.areaId) continue;
        }
        result.push({ id: node.originId, label: node.label });
      }
    }
    return result.sort((a, b) => a.label.localeCompare(b.label));
  }, [tree, filter.areaId]);

  const regions = useMemo<FilterOption[]>(() => {
    const result: FilterOption[] = [];
    for (const node of tree.values()) {
      if (node.nodeType === 'region') {
        // If country filter is active, only show regions under that country
        if (filter.countryId) {
          const parent = node.parentId ? tree.get(node.parentId) : null;
          if (parent?.originId !== filter.countryId) continue;
        }
        result.push({ id: node.originId, label: node.label });
      }
    }
    return result.sort((a, b) => a.label.localeCompare(b.label));
  }, [tree, filter.countryId]);

  return (
    <div className="flex flex-wrap items-center gap-3 p-3 border-b border-border bg-container-bg rounded-t-lg">
      {/* Area filter */}
      <SelectInput
        value={filter.areaId ?? ''}
        onChange={(e) => {
          const val = e.target.value ? Number(e.target.value) : undefined;
          onFilterChange({ areaId: val, countryId: undefined, regionId: undefined });
        }}
        displayEmpty
        fullWidth={false}
        options={[{ value: '', label: 'All Areas' }, ...areas.map((a) => ({ value: a.id, label: a.label }))]}
        sx={{ minWidth: 140 }}
      />

      {/* Country filter */}
      <SelectInput
        value={filter.countryId ?? ''}
        onChange={(e) => {
          const val = e.target.value ? Number(e.target.value) : undefined;
          onFilterChange({ ...filter, countryId: val, regionId: undefined });
        }}
        displayEmpty
        fullWidth={false}
        options={[{ value: '', label: 'All Countries' }, ...countries.map((c) => ({ value: c.id, label: c.label }))]}
        sx={{ minWidth: 140 }}
      />

      {/* Region filter */}
      <SelectInput
        value={filter.regionId ?? ''}
        onChange={(e) => {
          const val = e.target.value ? Number(e.target.value) : undefined;
          onFilterChange({ ...filter, regionId: val });
        }}
        displayEmpty
        fullWidth={false}
        options={[{ value: '', label: 'All Regions' }, ...regions.map((r) => ({ value: r.id, label: r.label }))]}
        sx={{ minWidth: 140 }}
      />

      {/* Clear button — only show when any filter is active */}
      {(filter.areaId || filter.countryId || filter.regionId || searchTerm) && (
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            onFilterChange({ areaId: undefined, countryId: undefined, regionId: undefined });
            onSearchChange('');
          }}
        >
          Clear
        </Button>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Text search */}
      <TextInput
        search
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search nodes..."
        sx={{ width: 192 }}
      />
    </div>
  );
}
